import { makeStyles } from '@material-ui/core'
import React from 'react'
import { tokens } from './tokens'
import { Container } from './Container'
import { SectionTitle } from './SectionTitle'

const STEPS = [
    {
        badge: 'Step 1',
        title: 'Configure a Solution',
        text: 'We start with designing the test solution and get you the budget.',
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
                <circle cx="12" cy="12" r="3" />
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06A2 2 0 1 1 7.04 4.3l.06.06A1.65 1.65 0 0 0 8.92 4.7 1.65 1.65 0 0 0 10 3.18V3a2 2 0 0 1 4 0v.09c0 .69.39 1.3 1 1.51.63.28 1.37.15 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06c-.48.45-.61 1.19-.33 1.82.21.61.82 1 1.51 1H21a2 2 0 0 1 0 4h-.09c-.69 0-1.3.39-1.51 1z" />
            </svg>
        ),
    },
    {
        badge: 'Step 2',
        title: 'Submit Design Files',
        text: 'Our professional team turns the design into accurate and precise specs.',
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
                <path d="M12 15V3M8 7l4-4 4 4" />
                <path d="M4 15v4a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-4" />
            </svg>
        ),
    },
    {
        badge: 'Step 3',
        title: 'We Build',
        text: 'Our expert test engineers now build your desired custom test solution.',
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
                <path d="M14.7 6.3a4 4 0 0 0-5.4 5.4l-6 6a1.5 1.5 0 0 0 2.1 2.1l6-6a4 4 0 0 0 5.4-5.4l-2.1 2.1-2-2z" />
            </svg>
        ),
    },
    {
        badge: 'Step 4',
        title: 'Solution Delivery',
        text: 'We do quick and accurate testing and deliver the best solution to our clients.',
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
                <rect x="3" y="7" width="14" height="10" rx="1" />
                <path d="M17 10l4-2v8l-4-2z" />
            </svg>
        ),
    },
]

const useStyles = makeStyles(() => ({
    section: { padding: '90px 0' },
    grid: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 10,
        flexWrap: 'wrap',
        '@media (max-width: 1024px)': { justifyContent: 'center' },
    },
    step: {
        flex: '1 1 0',
        textAlign: 'center',
        padding: '0 6px',
        '@media (max-width: 1024px)': { flex: '0 1 45%', marginBottom: 30 },
        '@media (max-width: 640px)': { flex: '0 1 100%' },
    },
    iconWrap: {
        position: 'relative',
        width: 96, height: 96,
        margin: '0 auto 20px',
        borderRadius: '50%',
        background: tokens.colorPrimary,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: tokens.shadowMd,
        color: '#fff',
        '& svg': { width: 36, height: 36 },
    },
    badge: {
        position: 'absolute',
        top: -10, right: -6,
        background: tokens.colorAccentRed,
        color: '#fff',
        fontSize: '.68rem',
        fontWeight: 700,
        padding: '3px 9px',
        borderRadius: 20,
        boxShadow: tokens.shadowSm,
    },
    title: { fontSize: '1.02rem', marginBottom: 8, color: tokens.colorBlack,fontFamily: tokens.fontBody, fontWeight: 700  },
    text: { fontSize: '.88rem', color: tokens.colorTextLight, maxWidth: 200, margin: '0 auto', fontFamily: tokens.fontBody },
    arrow: {
        color: tokens.colorPrimary,
        fontSize: '2.2rem',
        marginTop: 30,
        flex: '0 0 auto',
        opacity: .85,
        '@media (max-width: 1024px)': { display: 'none' },
    },
}))

export function Process() {
    const classes = useStyles()
    return (
        <section className={classes.section}>
            <Container>
                <SectionTitle>How to Work with Us</SectionTitle>
                <div className={classes.grid}>
                    {STEPS.map((step, i) => (
                        <React.Fragment key={step.title}>
                            <div className={classes.step}>
                                <div className={classes.iconWrap}>
                                    <span className={classes.badge}>{step.badge}</span>
                                    {step.icon}
                                </div>
                                <h3 className={classes.title}>{step.title}</h3>
                                <p className={classes.text}>{step.text}</p>
                            </div>
                            {i < STEPS.length - 1 && (
                                <div className={classes.arrow} aria-hidden="true">
                                    <svg viewBox="0 0 24 24" width="1em" height="1em" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M4 12h15M13 6l6 6-6 6" />
                                    </svg>
                                </div>
                            )}
                        </React.Fragment>
                    ))}
                </div>
            </Container>
        </section>
    )
}
